import { StateDefinitions } from '@iwentsourcing/core/storage/state-declaration.js';
import { EventDeclarations } from '@iwentsourcing/core/event-store/event-declarations.js';
import { StreamInterface } from '@iwentsourcing/core/stream/stream-interface.js';
import { StoredEvent } from '@iwentsourcing/core/event-store/stored-event.js';
import {
  StreamPushBatchOptions,
  StreamPushOptions,
} from '@iwentsourcing/core/stream/stream-push-options.js';
import { createRemoteObservedClient } from './create-remote-observed-client.js';
import { SnapshotClient } from '@iwentsourcing/core/storage/snapshot-client.js';
import { createRemoteSnapshotClient } from './create-remote-snapshot-client.js';
import { RemoteDescription } from './remote-description.js';
import { RpcClient } from '../api/create-rpc-client.js';
import { SessionManager } from '../auth/jwt-session.js';
import { Lazy } from '@iwentsourcing/core/lazy/lazy.js';
import { getStateData } from '@iwentsourcing/core/runtime/get-state-data-keys.js';
import { getOrFail } from '@iwentsourcing/core/utils/get-or-fail.js';
import { ObservedClient } from '@iwentsourcing/core/storage/observer/observed-client.js';
import { CustomError } from '@iwentsourcing/core/error/custom-error.js';
import { EventPushBatchItem } from '../api/event-push.js';
import { EventPushBatchResult } from '@iwentsourcing/core/event-store/event-push-batch-result.js';
import { EventPushBatchEvent } from '@iwentsourcing/core/event-store/event-push-batch-event.js';
import { GetOptions } from '@iwentsourcing/core/storage/get-options.js';
import { RangeOptions } from '@iwentsourcing/core/storage/range-options.js';
import { buildStreamKey } from '@iwentsourcing/core/stream/build-stream-name.js';
import { createLazy } from '@iwentsourcing/core/lazy/create-lazy.js';
import { ProcessorOperationResult } from '@iwentsourcing/core/sequencer/create-state-processor.js';
import { createEventStoreTransformer } from '@iwentsourcing/core/stream/event-store-transformer.js';
import { Telemetry } from '@iwentsourcing/core/tracer/telemetry.js';

export function createRemoteStreamInterface<
  TState extends StateDefinitions,
  TEvent extends EventDeclarations,
>(
  state: TState,
  event: TEvent,
  rpc: RpcClient,
  session: SessionManager,
  lazyVersion: Lazy<string>,
  streamIdentifier: RemoteDescription,
  telemetry: Telemetry,
): StreamInterface<TState, TEvent> {
  return {
    transformer: createEventStoreTransformer({
      telemetry: telemetry,
      patterns: { [streamIdentifier.pattern]: event },
    }),
    lazyName: createLazy(async () => {
      return buildStreamKey(streamIdentifier.pattern, {
        args: streamIdentifier.args,
        auth: await session.resolveAuth(),
      });
    }),
    state,
    async result(
      id: string,
      opts: GetOptions,
    ): Promise<ProcessorOperationResult | null> {
      return await rpc['process.result'].call(
        {
          realm: streamIdentifier.realm,
          tenant: streamIdentifier.tenant,
          version: await lazyVersion.resolve(),
          pattern: streamIdentifier.pattern,
          id: id,
          args: streamIdentifier.args,
          token: await session.resolveToken(),
        },
        opts.signal,
      );
    },
    async get(id: string, opts: GetOptions): Promise<StoredEvent | null> {
      const response = await rpc['event.get'].call(
        {
          realm: streamIdentifier.realm,
          tenant: streamIdentifier.tenant,
          version: await lazyVersion.resolve(),
          pattern: streamIdentifier.pattern,
          id: id,
          args: streamIdentifier.args,
          token: await session.resolveToken(),
        },
        opts.signal,
      );
      if (!response) {
        return null;
      }

      return response;
    },
    // async *iterate(
    //   date: Date | null,
    //   opts: IterateOptions,
    // ): AsyncGenerator<StoredEvent> {
    //   for await (const response of rpc['event.iterate'].call(
    //     {
    //       realm: streamIdentifier.realm,
    //       tenant: streamIdentifier.tenant,
    //       version: await lazyVersion.resolve(),
    //       pattern: streamIdentifier.pattern,
    //       date: date ?? undefined,
    //       args: streamIdentifier.args,
    //       token: await session.resolveToken(),
    //     },
    //     opts.signal,
    //   )) {
    //     yield response;
    //   }
    // },
    async *range(
      date: Date | null,
      options: RangeOptions,
    ): AsyncGenerator<StoredEvent> {
      for await (const response of rpc['event.range'].call(
        {
          realm: streamIdentifier.realm,
          tenant: streamIdentifier.tenant,
          version: await lazyVersion.resolve(),
          pattern: streamIdentifier.pattern,
          date: date ?? undefined,
          args: streamIdentifier.args,
          token: await session.resolveToken(),
        },
        options.signal,
      )) {
        yield response;
      }
    },
    async push<K extends string & keyof TEvent>(
      evt: K,
      data: TEvent[K]['type']['_output'],
      options: StreamPushOptions,
    ): Promise<StoredEvent> {
      const type = getOrFail(event, evt);
      const storedData = getStateData(type.type, data);
      const response = await rpc['event.push'].call(
        {
          realm: streamIdentifier.realm,
          tenant: streamIdentifier.tenant,
          version: await lazyVersion.resolve(),
          pattern: streamIdentifier.pattern,
          event: evt,
          data: storedData,
          annotations: options?.annotations,
          createdAt: options?.createdAt,
          id: options?.id,
          args: streamIdentifier.args,
          await: options?.await,
          token: await session.resolveToken(),
        },
        options.signal,
      );
      if (response === null) {
        throw new CustomError('unknown event', { event: evt });
      }
      return response;
    },
    async pushBatch(
      events: EventPushBatchEvent<TEvent, string & keyof TEvent>[],
      options: StreamPushBatchOptions,
    ): Promise<EventPushBatchResult> {
      const response = await rpc['event.pushBatch'].call(
        {
          realm: streamIdentifier.realm,
          tenant: streamIdentifier.tenant,
          version: await lazyVersion.resolve(),
          pattern: streamIdentifier.pattern,
          args: streamIdentifier.args,
          await: options?.await,
          token: await session.resolveToken(),
          events: events.map<EventPushBatchItem>((e) => {
            const type = getOrFail(event, e.evt);
            const storedData = getStateData(type.type, e.data);
            return {
              data: storedData,
              event: e.evt,
              annotations: e.annotations,
              createdAt: e.createdAt ?? new Date(),
              id: e.id,
            };
          }),
        },
        options.signal,
      );

      return {
        items: response.items,
      };
    },
    observe<K extends string & keyof TState>(
      name: K,
    ): ObservedClient<TState[K]> {
      return createRemoteObservedClient(
        state[name]!,
        rpc,
        session,
        lazyVersion,
        'stream',
        streamIdentifier,
        name,
        telemetry,
      );
    },
    snapshot<K extends string & keyof TState>(
      name: K,
    ): SnapshotClient<TState[K]> {
      return createRemoteSnapshotClient(
        state[name]!,
        rpc,
        session,
        lazyVersion,
        'stream',
        streamIdentifier,
        name,
        telemetry,
      );
    },
  };
}
